import { Box, Flex, Heading, Tag, Icon, Text } from '@chakra-ui/react';
import { SettingsIcon } from '@chakra-ui/icons';
import { CpuInfo, StatsForNerds } from '../../types/stats';
import { parsePercentage, getAlertColor } from '../../utils/stats';
import { PieChart, Pie, Cell, ResponsiveContainer, LineChart, Line, XAxis, YAxis } from 'recharts';
import { FaProcedures } from 'react-icons/fa';
import { FiCpu } from 'react-icons/fi';

interface CpuCardProps {
  data: CpuInfo;
  history: StatsForNerds[];
  paused: boolean;
}

export function CpuCard({ data, history, paused }: CpuCardProps) {
  const loadPercentage = parsePercentage(data.load);
  const alertColor = getAlertColor(loadPercentage);
  
  const chartData = [
    { name: 'Used', value: loadPercentage, fill: '#3182CE' }, // blue.500
    { name: 'Free', value: 100 - loadPercentage, fill: '#E2E8F0' } // gray.200
  ];

  const sparklineData = history.slice(-10).map((stats, index) => ({
    index,
    load: parsePercentage(stats.cpu.load)
  }));

  return (
    <Box borderWidth="1px" borderRadius="2xl" p={4} position="relative" overflow="hidden">
      {paused && (
        <Tag borderRadius={"2xl"} position="absolute" top={2} right={2} colorScheme="yellow">Paused</Tag>
      )}
      
      <Flex as="header" alignItems="center" justifyContent="space-between" pb={2}>
        <Heading as="h3" size="sm" display="flex" alignItems="center" gap={2}>
          {/* <Icon as={SettingsIcon} /> */}
          <FiCpu/>
          CPU
        </Heading>
        <Tag borderRadius={"2xl"} colorScheme={alertColor === 'danger' ? 'red' : alertColor === 'warning' ? 'yellow' : 'green'}>
          {data.load}
        </Tag>
      </Flex>
      
      <Flex direction={{ base: 'column', md: 'row' }} gap={4}>
        {/* CPU Gauge */}
        <Flex direction="column" alignItems="center">
          <Box w="100px" h="100px" position="relative">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={30}
                  outerRadius={40}
                  startAngle={90}
                  endAngle={-270}
                  dataKey="value"
                  stroke="none"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <Flex position="absolute" top={0} left={0} right={0} bottom={0} alignItems="center" justifyContent="center">
              <Text fontSize="lg" fontWeight="bold" fontFamily="mono">{Math.round(loadPercentage)}%</Text>
            </Flex>
          </Box>
        </Flex>

        {/* CPU Info & Sparkline */}
        <Box flex={1}>
          <Box fontSize="sm">
            <Text color="gray.500">Model:</Text>
            <Text fontFamily="mono" fontSize="xs" isTruncated>{data.model}</Text>
          </Box>
          <Flex justifyContent="space-between" fontSize="sm">
            <Text color="gray.500">Cores:</Text>
            <Text fontFamily="mono">{data.cores}</Text>
          </Flex>
          <Flex justifyContent="space-between" fontSize="sm">
            <Text color="gray.500">Speed:</Text>
            <Text fontFamily="mono">{data.speed} GHz</Text>
          </Flex>
          
          {/* Sparkline */}
          {sparklineData.length > 1 && (
            <Box h="48px" mt={2}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={sparklineData}>
                  <XAxis dataKey="index" hide />
                  <YAxis hide domain={[0, 100]} />
                  <Line
                    type="monotone"
                    dataKey="load"
                    stroke="#3182CE" // blue.500
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          )}
        </Box>
      </Flex>
    </Box>
  );
}
